import React from 'react';
import PropTypes from 'prop-types';
import { Common } from 'ws-scripts/modules/common';
import events from './events';
import './PhotoSwipe.scss';
import CommonButton from './CommonButton';
import DDCPlayerVideoComponent from '../DDCPlayerVideoComponent';
import IFrameVideoComponent from '../IFrameVideoComponent';
import Homenet360Component from '../homenet/Homenet360Component';
import Arrow from '../Arrow';

const pauseVideo = () => {
	DDC.Video.getAllPlayers().forEach(player => {
		if (player.getState() === 'PLAYING') {
			player.pause();
		}
	});
};

class Photoswipe extends React.Component {
	componentDidMount() {
		const {
			modalSettings: { modalIsOpen }
		} = this.props;
		if (modalIsOpen) {
			this.openPhotoSwipe(this.props);
		}
	}

	componentWillReceiveProps(nextProps) {
		const {
			modalSettings: { modalIsOpen }
		} = nextProps;
		if (!modalIsOpen) {
			this.openPhotoSwipe(nextProps);
		} else {
			const {
				props: { items }
			} = this;
			const newItems = nextProps.items;
			let hasChanges = false;

			if (items.length !== newItems.length) {
				hasChanges = true;
			}

			items.forEach((item, index) => {
				if (newItems[index]) {
					if (
						newItems[index].id !== item.id ||
						newItems[index].src !== item.src
					) {
						hasChanges = true;
					}
				}
			});

			if (hasChanges) {
				this.updateItems(newItems);
			}
		}
	}

	componentWillUnmount() {
		this.closePhotoSwipe();
	}

	openPhotoSwipe(props) {
		const { items, options } = props;
		const { pswpElement } = this;

		/* eslint no-undef: 0 */
		this.photoSwipe = new PhotoSwipe(
			pswpElement,
			PhotoSwipeUI_Default,
			items,
			options
		);

		events.forEach(event => {
			const callback = props[event];
			if (callback || event === 'destroy') {
				const self = this;
				this.photoSwipe.listen(event, (...args) => {
					if (callback) {
						args.unshift(this);
						callback(...args);
					}
					if (event === 'destroy') {
						self.handleClose();
					}
				});
			}
		});

		this.photoSwipe.listen('imageLoadComplete', (index, item) => {
			if (!item.resize) return;
			const img = new Image();
			img.src = item.src;

			/* eslint-disable no-param-reassign */
			img.onload = () => {
				item.w = img.naturalWidth;
				item.h = img.naturalHeight;
				item.resize = false;
				this.photoSwipe.invalidateCurrItems();
				this.photoSwipe.updateSize(true);
			};
		});

		this.photoSwipe.init();
	}

	updateItems(items = []) {
		this.photoSwipe.items.length = 0;

		items.forEach(item => {
			this.photoSwipe.items.push(item);
		});

		this.photoSwipe.invalidateCurrItems();
		this.photoSwipe.updateSize(true);
	}

	closePhotoSwipe() {
		if (!this.photoSwipe) {
			return;
		}
		this.photoSwipe.close();
	}

	handleClose() {
		const { onClose, updateModalSettings, trackGa } = this.props;
		updateModalSettings({ modalIsOpen: false });
		if (onClose) {
			onClose();
			trackGa({
				element: 'Modal Gallery Close Button',
				result: 'Closes gallery modal',
				widgetState: 'Gallery Modal'
			});
		}
	}

	trackZoomEvent(el = null) {
		const { trackGa } = this.props;
		const zoomed = this.photoSwipe.getZoomLevel() >= 1;

		let element = el;

		if (!el) {
			element = zoomed ? 'Zoom out Button' : 'Zoom in Button';
		}

		trackGa({
			element,
			result: zoomed ? 'Zooms out of photo' : 'Zooms into photo',
			widgetState: 'Gallery Modal'
		});
	}

	render() {
		const {
			className,
			updateModalSettings,
			trackGa,
			modalSettings,
			video,
			widgetInfo,
			viewportWidth,
			viewportHeight,
			mobileOrientationIsPortrait,
			labels
		} = this.props;

		const { fullScreen, videoIsOpen, spin360IsOpen } = modalSettings;
		const { deviceType } = widgetInfo;
		const showGallery = videoIsOpen || spin360IsOpen;
		const show360Spin =
			widgetInfo.vehicle &&
			widgetInfo.vehicle.fyuseUid &&
			widgetInfo.vehicle.fyuseUid.length > 0;

		let videoComponent;
		if (video && video.provider && video.provider === 'DCPAIS') {
			videoComponent = (
				<IFrameVideoComponent
					video={video}
					modalSettings={modalSettings}
					widgetInfo={widgetInfo}
					viewportWidth={viewportWidth}
					viewportHeight={viewportHeight}
					mobileOrientationIsPortrait={mobileOrientationIsPortrait}
				/>
			);
		} else {
			videoComponent = (
				<DDCPlayerVideoComponent
					video={video}
					modalSettings={modalSettings}
					widgetInfo={widgetInfo}
					viewportWidth={viewportWidth}
					viewportHeight={viewportHeight}
					mobileOrientationIsPortrait={mobileOrientationIsPortrait}
				/>
			);
		}

		return (
			<div
				className={['pswp', className].join(' ')}
				tabIndex="-1"
				role="dialog"
				aria-hidden="true"
				ref={node => {
					this.pswpElement = node;
				}}
			>
				<div className="media-buttons-toolbar fixed-top w-75 text-left pt-1">
					<CommonButton
						buttonName={labels.get('PHOTOS')}
						bsSize={deviceType === 'DESKTOP' ? 'small' : null}
						iconClass="ddc-icon-images"
						buttonClasses={
							deviceType !== 'DESKTOP' ? 'text-nowrap' : ''
						}
						onClick={() => {
							updateModalSettings({
								videoIsOpen: false,
								spin360IsOpen: false
							});
							pauseVideo();
							trackGa({
								element: 'Photo Count Button',
								elementCta: 'Photo Count',
								result: 'Opens photos',
								widgetState: 'Gallery Modal'
							});
						}}
					/>
					{video != null ? (
						<CommonButton
							buttonName={labels.get('VIDEO')}
							bsSize={deviceType === 'DESKTOP' ? 'small' : null}
							iconClass="ddc-icon-play2"
							buttonClasses={
								deviceType !== 'DESKTOP' ? 'text-nowrap' : ''
							}
							onClick={() => {
								updateModalSettings({
									videoIsOpen: true,
									spin360IsOpen: false
								});
								trackGa({
									element: 'Video Button',
									elementCta: 'Video',
									result: 'Opens video',
									widgetState: 'Gallery Modal'
								});
							}}
						/>
					) : null}
					{show360Spin ? (
						<Common
							render={({ flags }) => {
								if (flags['ws-vehicle-media-homenet']) {
									return (
										<CommonButton
											buttonName="360"
											bsSize={
												deviceType === 'DESKTOP'
													? 'small'
													: null
											}
											iconClass="ddc-icon-360-spin"
											buttonClasses={
												deviceType !== 'DESKTOP'
													? 'text-nowrap'
													: ''
											}
											onClick={() => {
												updateModalSettings({
													videoIsOpen: false,
													spin360IsOpen: true
												});
												pauseVideo();
												trackGa({
													element:
														'Homenet 360 Button',
													elementCta: 'Homenet',
													result:
														'Opens Homenet 360 spin',
													widgetState: 'Gallery Modal'
												});
											}}
										/>
									);
								} else {
									return null;
								}
							}}
						/>
					) : null}
				</div>

				<div className="pswp__bg" />
				<div className="pswp__scroll-wrap">
					<div
						className={`pswp__container ${
							showGallery ? 'hide' : null
						}`}
						onClick={() => this.trackZoomEvent('Photo Carousel')}
						role="button"
						tabIndex="-1"
						onKeyDown={() => {
							// TODO: figure out what to do here because of jsx-a11y/click-events-have-key-events
						}}
					>
						<div className="pswp__item" />
						<div className="pswp__item" />
						<div className="pswp__item" />
					</div>
					<div className="pswp__ui pswp__ui--hidden">
						<div className="pswp__top-bar">
							<button
								className="pswp__button pswp__button--close"
								title={`${labels.get('CLOSE')} (Esc)`}
							/>
							<button
								className="pswp__button pswp__button--fs"
								title={labels.get('TOGGLE_FULLSCREEN')}
								onClick={() => {
									const fullscreenMode = fullScreen
										? !fullScreen
										: true;
									updateModalSettings({
										fullScreen: fullscreenMode
									});

									trackGa({
										element: 'Fullscreen button',
										result: !fullscreenMode
											? 'Exits fullscreen mode'
											: 'Enters fullscreen mode',
										widgetState: 'Gallery Modal'
									});
								}}
							/>
							<button
								className={`pswp__button pswp__button--zoom ${
									showGallery ? 'hidden' : null
								}`}
								title={labels.get('ZOOM_IN_OUT')}
								onClick={() => this.trackZoomEvent()}
							/>
							<div className="pswp__preloader">
								<div className="pswp__preloader__icn">
									<div className="pswp__preloader__cut">
										<div className="pswp__preloader__donut" />
									</div>
								</div>
							</div>
						</div>
						<Arrow
							direction="left"
							onClickEvent={() => {
								trackGa({
									element: 'Modal Gallery Previous Button',
									result: 'Move to previous photo',
									widgetState: 'Gallery Modal'
								});
							}}
							classes={[
								'pswp__button',
								'pswp__button--arrow--left',
								`${showGallery ? 'hidden' : ''}`,
								`${deviceType === 'DESKTOP' ? 'visible' : ''}`
							]}
						/>
						<Arrow
							direction="right"
							onClickEvent={() => {
								trackGa({
									element: 'Modal Gallery Next Button',
									result: 'Move to next photo',
									widgetState: 'Gallery Modal'
								});
							}}
							classes={[
								'pswp__button',
								'pswp__button--arrow--right',
								`${showGallery ? 'hidden' : ''}`,
								`${deviceType === 'DESKTOP' ? 'visible' : ''}`
							]}
						/>
						<div className="pswp__caption">
							<div className="pswp__caption__center" />
						</div>
						<div
							className={`pswp__counter text-center fixed-bottom bg-black ${
								showGallery ? 'hidden' : ''
							}`}
						/>
					</div>
				</div>
				{videoComponent}
				{show360Spin ? (
					<Homenet360Component
						modalSettings={modalSettings}
						fyuseUid={widgetInfo.vehicle.fyuseUid}
						trackGa={trackGa}
					/>
				) : null}
			</div>
		);
	}
}

Photoswipe.propTypes = {
	items: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.number,
			src: PropTypes.string
		})
	).isRequired,
	/* eslint react/no-unused-prop-types: 0 */
	options: PropTypes.shape({}),
	onClose: PropTypes.func,
	className: PropTypes.string,
	updateModalSettings: PropTypes.func.isRequired,
	trackGa: PropTypes.func.isRequired,
	modalSettings: PropTypes.shape({
		zoomedIn: PropTypes.bool,
		modalIsOpen: PropTypes.bool,
		fullScreen: PropTypes.bool,
		videoIsOpen: PropTypes.bool,
		spin360IsOpen: PropTypes.bool
	}).isRequired,
	widgetInfo: PropTypes.shape({
		deviceType: PropTypes.string,
		vehicle: PropTypes.shape({
			fyuseUid: PropTypes.string
		}).isRequired
	}).isRequired,
	viewportWidth: PropTypes.number.isRequired,
	viewportHeight: PropTypes.number.isRequired,
	video: PropTypes.shape({
		provider: PropTypes.string
	}),
	mobileOrientationIsPortrait: PropTypes.bool.isRequired,
	labels: PropTypes.shape({
		get: PropTypes.func
	}).isRequired
};

Photoswipe.defaultProps = {
	options: {},
	onClose: () => {},
	className: '',
	video: null
};

export default Photoswipe;



// WEBPACK FOOTER //
// ./src/components/photoswipe/Photoswipe.jsx